<template>
  <settings-chrome
    kicker="Settings"
    title="Accounting"
    sub="Defaults for invoices, and how customers pay you."
    form-id="accounting-settings-form"
    :dirty="dirty"
    :saving="isSaving"
    @discard="revert"
    @allow-leave="dirty = false"
  >
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="accounting-settings-form"
        class="st-body"
        @submit.prevent="handleSubmit(submit)"
      >
        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Invoices</h2>
              <p class="st-lead">
                Starting values on every new invoice. You can still change them
                per invoice.
              </p>
            </div>
          </div>
          <div class="st-pair">
            <label class="st-field">
              VAT rate
              <p class="st-hint">Percent added to prices. 23 is usual in Ireland.</p>
              <ValidationProvider
                rules="required|min_value:0"
                v-slot="{ errors }"
              >
                <fg-input
                  class="mb-0"
                  type="number"
                  min="0"
                  step="0.1"
                  v-model="invoicing.defaultVatRate"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <label class="st-field">
              Days to pay
              <p class="st-hint">
                How long the customer has after you send the invoice.
              </p>
              <ValidationProvider
                rules="required|numeric|min_value:1"
                v-slot="{ errors }"
              >
                <fg-input
                  class="mb-0"
                  type="number"
                  min="1"
                  step="1"
                  v-model="invoicing.defaultDaysToPay"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
          </div>
        </section>

        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Payments</h2>
              <p class="st-lead">
                Bank details for invoices, and card payments when people book.
              </p>
            </div>
            <router-link
              class="st-chip st-chip-sm"
              to="/settings/accounting/payments"
            >
              Set up payments
            </router-link>
          </div>
          <div>
            <div class="st-row">
              <span class="st-row-label">Bank details on invoices</span>
              <span
                class="st-pill"
                :class="bankConfigured ? 'is-on' : 'is-off'"
              >
                {{ bankConfigured ? "Set" : "Not set" }}
              </span>
            </div>
            <div class="st-row">
              <span class="st-row-label">Card payments</span>
              <span
                class="st-pill"
                :class="onlineOn ? 'is-on' : 'is-off'"
              >
                {{ onlineOn ? "On" : "Off" }}
              </span>
            </div>
            <div v-if="onlineOn" class="st-row">
              <span class="st-row-label">Stripe</span>
              <span
                class="st-pill"
                :class="stripeOn ? 'is-on' : 'is-off'"
              >
                {{ stripeOn ? "Connected" : "Not connected" }}
              </span>
            </div>
            <div class="st-row">
              <span class="st-row-label">Overdue invoice emails</span>
              <span
                class="st-pill"
                :class="remindersOn ? 'is-on' : 'is-off'"
              >
                {{ remindersOn ? "On" : "Off" }}
              </span>
            </div>
          </div>
        </section>

        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Customer statements</h2>
              <p class="st-lead">
                Email customers a list of what they still owe.
              </p>
            </div>
          </div>
          <div class="st-field">
            <span>Send statements</span>
            <div class="st-seg" role="radiogroup" aria-label="Send statements">
              <button
                type="button"
                class="st-seg-btn"
                :class="{ 'is-on': statements.enabled === true }"
                @click="statements.enabled = true"
              >
                Yes
              </button>
              <button
                type="button"
                class="st-seg-btn"
                :class="{ 'is-on': statements.enabled === false }"
                @click="statements.enabled = false"
              >
                No
              </button>
            </div>
          </div>
        </section>
      </form>
    </ValidationObserver>
  </settings-chrome>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { DEFAULT_SETTINGS } from "@/store/userSettings";
import SettingsChrome from "@/components/settings/SettingsChrome";

export default {
  components: { SettingsChrome },
  data() {
    return {
      isSaving: false,
      dirty: false,
      hydrating: false,
      invoicing: { ...DEFAULT_SETTINGS.invoicing },
      statements: { ...DEFAULT_SETTINGS.customerStatements }
    };
  },
  computed: {
    ...mapGetters({ userSettings: "userSettings" }),
    bankConfigured() {
      const bank =
        this.userSettings &&
        this.userSettings.company &&
        this.userSettings.company.bankDetails;
      return !!(bank && bank.accountName);
    },
    onlineOn() {
      return !!(this.userSettings && this.userSettings.onlinePaymentsEnabled);
    },
    stripeOn() {
      return !!(this.userSettings && this.userSettings.stripeUserId);
    },
    remindersOn() {
      return !!(
        this.userSettings &&
        this.userSettings.paymentReminders &&
        this.userSettings.paymentReminders.enabled
      );
    }
  },
  watch: {
    userSettings: {
      immediate: true,
      deep: true,
      handler() {
        if (this.dirty || this.isSaving) return;
        this.resetModel();
      }
    },
    invoicing: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    },
    statements: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    }
  },
  created() {
    this.fetchUserSettings();
  },
  methods: {
    ...mapActions(["fetchUserSettings", "updateUserSettings"]),
    resetModel() {
      this.hydrating = true;
      const settings = this.userSettings || {};
      this.invoicing = {
        ...DEFAULT_SETTINGS.invoicing,
        ..._.cloneDeep(settings.invoicing || {})
      };
      this.statements = {
        ...DEFAULT_SETTINGS.customerStatements,
        ..._.cloneDeep(settings.customerStatements || {})
      };
      this.dirty = false;
      this.$nextTick(() => {
        this.hydrating = false;
      });
    },
    revert() {
      this.dirty = false;
      this.resetModel();
    },
    async submit() {
      this.isSaving = true;
      try {
        await this.updateUserSettings({
          ...this.userSettings,
          invoicing: this.invoicing,
          customerStatements: this.statements
        });
        this.dirty = false;
        this.resetModel();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.saveSettingsSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.saveSettingsFailure") +
            ` ${(error && error.message) || ""}`.trim()
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>
